/**
 * Pure helpers turning a {@link CollectorStatusResponse} into the at-a-glance
 * collector-health summary the footer indicator and the status page render.
 *
 * The collector polls the CC0 feed every ~5 min (see COLLECTOR.md), so the
 * server-computed `dataAgeSeconds` (now − last successful poll) is the primary
 * staleness signal — we trust it over the client clock. Level buckets mirror the
 * feed-freshness buckets used elsewhere (one missed poll still lands "live").
 *
 * Deterministic, no clock, no DOM.
 */

import type { CollectorStatusResponse } from "@/api/generated/model";
import { formatAgo } from "@/lib/time";

export type CollectorLevel = "live" | "delayed" | "down" | "unknown";

const MINUTE = 60;
const LIVE_MAX_SECONDS = 10 * MINUTE; // at most one poll late
const DELAYED_MAX_SECONDS = 30 * MINUTE; // a few polls missed — degraded

const LEVEL_WORD: Record<CollectorLevel, string> = {
  live: "In tempo reale",
  delayed: "Dati in ritardo",
  down: "Aggiornamento interrotto",
  unknown: "Stato sconosciuto",
};

export interface CollectorStatusSummary {
  level: CollectorLevel;
  /** Short Italian state word (never colour-only — pairs with the dot). */
  word: string;
  /** Age phrase for the last successful poll ("3 min fa"), or `null` if none. */
  ageLabel: string | null;
}

/**
 * Derive the health level. `dataAgeSeconds === null` (no successful poll since
 * startup) ⇒ `unknown`. An unhealthy collector or any consecutive failures
 * downgrades to at least `delayed`, escalating to `down` once the last success
 * is older than the delayed window.
 */
export function collectorLevel(status: CollectorStatusResponse): CollectorLevel {
  const age = status.dataAgeSeconds;
  if (age === null || !Number.isFinite(age) || age < 0) return "unknown";

  const failing = !status.isHealthy || status.consecutiveFailures > 0;
  if (failing) return age > DELAYED_MAX_SECONDS ? "down" : "delayed";

  if (age <= LIVE_MAX_SECONDS) return "live";
  if (age <= DELAYED_MAX_SECONDS) return "delayed";
  return "down";
}

/**
 * Whether the collector has fallen behind enough to warn the user about — i.e.
 * the level is `delayed` or `down`. Drives the {@link StaleDataBanner}.
 *
 * `unknown` (no successful poll since startup — e.g. right after a deploy) is
 * deliberately NOT stale: we don't cry wolf on a fresh restart (#89 Q1). `live`
 * is obviously not stale. Reuses {@link collectorLevel}'s bucketing (which
 * already folds `consecutiveFailures` / `isHealthy` in), so there are no new
 * thresholds to keep in sync.
 */
export function isStale(status: CollectorStatusResponse): boolean {
  const level = collectorLevel(status);
  return level === "delayed" || level === "down";
}

/** Age phrase for the last successful poll, or `null` when there has been none. */
export function collectorAgeLabel(status: CollectorStatusResponse): string | null {
  const age = status.dataAgeSeconds;
  if (age === null || !Number.isFinite(age) || age < 0) return null;
  return formatAgo(age);
}

/** Full at-a-glance summary for the indicator. */
export function collectorSummary(status: CollectorStatusResponse): CollectorStatusSummary {
  const level = collectorLevel(status);
  return { level, word: LEVEL_WORD[level], ageLabel: collectorAgeLabel(status) };
}

/** Format a "patients/hour" rate or row count compactly; `null` → "—". */
export function formatCount(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return String(value);
}

/** Format a lag/age in seconds as a short human string, or "—" when null. */
export function formatSeconds(value: number | null): string {
  if (value === null || !Number.isFinite(value) || value < 0) return "—";
  if (value < MINUTE) return `${Math.round(value)} s`;
  return formatAgo(value).replace(/ fa$/, "");
}
